import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { useFlota, type VaultPublico, type VaultCerrada } from "../hooks/useFlota";
import { REDES, redesActivas, type RedKeel } from "../config/networks";
import { num, antiguedad, duracion } from "../lib/formato";
import { ParMonedas, IconoRed } from "./Monedas";
import { useSeries, type DiaSerie } from "../hooks/useSeries";
import { useT } from "../lib/i18n";
import { DetalleVault } from "./DetalleVault";
import { urlPool, sitioPool, urlArkham } from "../lib/enlaces";
import "../landing.css";
import "../dashboard.css";

type Periodo = "dia" | "semana" | "mes";

/** Suma varias series (una por red) en una sola, dia a dia. */
function fundirDias(arrays: DiaSerie[][]): DiaSerie[] {
  const m = new Map<string, DiaSerie>();
  for (const arr of arrays) for (const d of arr) {
    const e = m.get(d.d) ?? { d: d.d, rebal: 0, opened: 0, fees: 0, recover: 0 };
    e.rebal += d.rebal; e.opened += d.opened; e.fees += d.fees; e.recover += d.recover;
    m.set(d.d, e);
  }
  return [...m.values()].sort((a, b) => (a.d < b.d ? -1 : 1));
}

export type Barra = { label: string; rebal: number; opened: number; fees: number; recover: number };

/** Agrupa los dias en cubos por periodo y se queda con los ultimos N. */
function agrupar(dias: DiaSerie[], periodo: Periodo): Barra[] {
  const tope = periodo === "dia" ? 30 : periodo === "semana" ? 16 : 12;
  const m = new Map<string, { orden: number; b: Barra }>();
  for (const d of dias) {
    const t = new Date(d.d + "T00:00:00Z").getTime();
    let key: string, label: string, orden: number;
    if (periodo === "dia") {
      key = d.d; orden = t; label = `${d.d.slice(8)}/${d.d.slice(5, 7)}`;
    } else if (periodo === "semana") {
      const w = Math.floor(t / (7 * 864e5)); key = String(w); orden = w;
      const ini = new Date(w * 7 * 864e5).toISOString();
      label = `${ini.slice(8, 10)}/${ini.slice(5, 7)}`;
    } else {
      key = d.d.slice(0, 7); orden = t; label = `${key.slice(5)}/${key.slice(2, 4)}`;
    }
    const e = m.get(key) ?? { orden, b: { label, rebal: 0, opened: 0, fees: 0, recover: 0 } };
    e.b.rebal += d.rebal; e.b.opened += d.opened; e.b.fees += d.fees; e.b.recover += d.recover;
    m.set(key, e);
  }
  return [...m.values()].sort((a, b) => a.orden - b.orden).slice(-tope).map((x) => x.b);
}

/**
 * Dashboard del protocolo. Es la foto de TODO lo que Keellabs gestiona ahora mismo, leida directa
 * de la cadena (useFlota, sin backend intermediario). Mismo espiritu que la portada publica, pero
 * agregado y para mirar de un vistazo: cuanto capital, cuantas posiciones, cuantas dentro de rango,
 * cuanto han ganado, y como se reparte por red y por par.
 *
 * PARTE 1 (esta): KPIs + distribucion + tabla, todo en vivo. Las SERIES HISTORICAS (volumen,
 * comisiones y rebalanceos por dia) llegan en la parte 2, agregando los eventos ya indexados.
 */

/** Nombre de la RED (no del DEX) para el selector: «Robinhood», no «Acciones». */
const NOMBRE_RED: Record<RedKeel["clave"], string> = {
  arbitrum: "Arbitrum", robinhood: "Robinhood", hyperevm: "HyperEVM", bnb: "BNB Chain", base: "Base", ethereum: "Ethereum",
};

/** Cifra que sube desde 0 al aparecer (o al cambiar). Sin animación si el sistema pide menos movimiento. */
function Contador({ n }: { n: number }) {
  const [v, setV] = useState(n);
  const desde = useRef(0);
  useEffect(() => {
    const quieto = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (quieto || n === desde.current) { setV(n); desde.current = n; return; }
    const ini = desde.current, t0 = performance.now(), dur = 900;
    let raf = 0;
    const paso = (ahora: number) => {
      const k = Math.min(1, (ahora - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      setV(Math.round(ini + (n - ini) * e));
      if (k < 1) raf = requestAnimationFrame(paso); else desde.current = n;
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [n]);
  return <>{v}</>;
}

/** «hace 12 s» que se refresca solo: dice cuándo se leyó la cadena por última vez. */
function Hace({ ts }: { ts: number }) {
  const t = useT();
  const [ahora, setAhora] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setAhora(Date.now()), 1000); return () => clearInterval(iv); }, []);
  if (!ts) return <>—</>;
  const s = Math.max(0, Math.round((ahora - ts) / 1000));
  return <>{s < 60 ? t("hace {n} s", { n: s }) : t("hace {n} min", { n: Math.floor(s / 60) })}</>;
}

const CHAIN = (clave: RedKeel["clave"]) => REDES[clave].etiqueta.split(" · ")[1] ?? REDES[clave].etiqueta;

/** Dinero compacto: $2,36M · $106,5k · $842. Coherente con como la portada dice el total. */
function money(x: number | undefined): string {
  if (x === undefined || !isFinite(x)) return "—";
  const s = Math.abs(x);
  if (s >= 1e6) return `$${num(x / 1e6, 2)}M`;
  if (s >= 1e3) return `$${num(x / 1e3, 1)}k`;
  return `$${num(x, s < 100 ? 2 : 0)}`;
}

/** Igual que en la portada: pinta lo que ya llego cuando entra en pantalla. */
function useReveal(dep: unknown) {
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const nodo = raiz.current;
    if (!nodo) return;
    const objetivos = Array.from(nodo.querySelectorAll<HTMLElement>(".reveal"));
    if (!("IntersectionObserver" in window)) { objetivos.forEach((e) => e.classList.add("in")); return; }
    const obs = new IntersectionObserver(
      (filas) => filas.forEach((f) => f.isIntersecting && f.target.classList.add("in")),
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" },
    );
    objetivos.forEach((e) => obs.observe(e));
    return () => obs.disconnect();
  }, [dep]);
  return raiz;
}

type Estado = "todas" | "rango" | "fuera" | "cerradas";

/** Iconos de las tarjetas KPI. Trazo fino, un glifo por métrica. */
function IcoKpi({ n }: { n: string }) {
  const g: Record<string, ReactNode> = {
    capital: <><ellipse cx="12" cy="6" rx="7.5" ry="3" /><path d="M4.5 6v6c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3V6" /><path d="M4.5 12v6c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-6" /></>,
    posiciones: <><path d="M3 8c2 0 2 1.5 4 1.5S11 8 13 8s2 1.5 4 1.5S19 8 21 8" /><path d="M3 13c2 0 2 1.5 4 1.5S11 13 13 13s2 1.5 4 1.5S19 13 21 13" /><path d="M3 18c2 0 2 1.5 4 1.5S11 18 13 18s2 1.5 4 1.5S19 18 21 18" /></>,
    rango: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>,
    ganancias: <><polyline points="3 17 9 11 13 15 21 6" /><polyline points="15 6 21 6 21 12" /></>,
    medio: <><path d="M4 16a8 8 0 1 1 16 0" /><line x1="12" y1="16" x2="15.5" y2="10.5" /><circle cx="12" cy="16" r="1.3" fill="currentColor" stroke="none" /></>,
    comision: <><line x1="19" y1="5" x2="5" y2="19" /><circle cx="7" cy="7" r="2.3" /><circle cx="17" cy="17" r="2.3" /></>,
    barras: <><line x1="5" y1="21" x2="5" y2="13" /><line x1="12" y1="21" x2="12" y2="4" /><line x1="19" y1="21" x2="19" y2="9" /><line x1="2.5" y1="21" x2="21.5" y2="21" /></>,
    redes: <><line x1="4" y1="7" x2="15" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="11" y2="17" /></>,
    rebal: <><path d="M21 12a9 9 0 1 1-2.64-6.36" /><polyline points="21 4 21 9 16 9" /></>,
    nuevas: <><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></>,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {g[n]}
    </svg>
  );
}

export function Dashboard({ alVolver }: { alVolver: () => void }) {
  const t = useT();
  const flota = useFlota();
  const [red, setRed] = useState<"todas" | RedKeel["clave"]>("todas");
  const [estado, setEstado] = useState<Estado>("todas");
  // Piscina abierta en modo lectura dentro del Dashboard (la misma ficha que ve su dueño, sin acciones).
  const [viendo, setViendo] = useState<VaultPublico | null>(null);

  const todos = flota.data?.vaults ?? [];
  const vaults = useMemo(
    () => (red === "todas" ? todos : todos.filter((v) => v.red === red)),
    [todos, red],
  );

  const raiz = useReveal(`${vaults.length}-${red}`);

  // ---- KPIs (sobre lo filtrado por red) --------------------------------------
  const k = useMemo(() => {
    const total = vaults.reduce((s, v) => s + v.valor, 0);
    const enRango = vaults.filter((v) => v.enRango).length;
    const conGanado = vaults.filter((v) => v.ganado !== undefined);
    // «Ganancias generadas» es lo repartido en toda la vida del protocolo: lo de las piscinas vivas
    // MÁS lo que dejaron las ya cerradas. Respeta el filtro de red igual que el resto de KPIs.
    const cerrado = red === "todas"
      ? (flota.data?.ganadoCerrado ?? 0)
      : (flota.data?.ganadoCerradoPorRed?.[red] ?? 0);
    const ganado = conGanado.reduce((s, v) => s + (v.ganado ?? 0), 0) + cerrado;
    const redes = new Set(vaults.map((v) => v.red));
    // VOLUMEN = capital total que ha ENTRADO a los vaults a lo largo del tiempo, medido a coste
    // (`costBasisRef`, en el estable ≈ $): el de las piscinas vivas MÁS el de las ya cerradas. No es
    // el TVL (valor de mercado de ahora): es cuánto capital ha movido el protocolo, acumulado.
    const cerradasRed = (flota.data?.cerradas ?? []).filter((c) => red === "todas" || c.red === red);
    const volumen = vaults.reduce((s, v) => s + (v.capital ?? 0), 0)
      + cerradasRed.reduce((s, c) => s + (c.capital ?? 0), 0);
    return {
      total, enRango, ganado, volumen,
      n: vaults.length,
      fuera: vaults.length - enRango,
      pctRango: vaults.length ? Math.round((enRango / vaults.length) * 100) : 0,
      medio: vaults.length ? total / vaults.length : 0,
      redes: redes.size,
    };
  }, [vaults, red, flota.data]);

  // ---- capital por red (para el desglose del KPI de TVL) ---------------------
  const porRed = useMemo(() => {
    const m = new Map<RedKeel["clave"], { valor: number; n: number; rango: number }>();
    for (const v of todos) {
      const e = m.get(v.red) ?? { valor: 0, n: 0, rango: 0 };
      e.valor += v.valor; e.n += 1; e.rango += v.enRango ? 1 : 0;
      m.set(v.red, e);
    }
    return [...m.entries()].sort((a, b) => b[1].valor - a[1].valor);
  }, [todos]);
  const maxRedValor = Math.max(1, ...porRed.map(([, e]) => e.valor));

  // ---- capital por par (grafico de barras) -----------------------------------
  const porPar = useMemo(() => {
    const m = new Map<string, { par: string; s0: string; s1: string; red: RedKeel["clave"]; valor: number }>();
    for (const v of vaults) {
      const key = `${v.par}·${v.red}`;
      const e = m.get(key) ?? { par: v.par, s0: v.s0, s1: v.s1, red: v.red, valor: 0 };
      e.valor += v.valor; m.set(key, e);
    }
    return [...m.values()].sort((a, b) => b.valor - a.valor).slice(0, 8);
  }, [vaults]);
  const maxPar = Math.max(1, ...porPar.map((p) => p.valor));

  // ---- posiciones por red (barras apiladas rango/fuera) ----------------------
  const estadoPorRed = useMemo(() => {
    const base = red === "todas" ? todos : vaults;
    const m = new Map<RedKeel["clave"], { rango: number; fuera: number }>();
    for (const v of base) {
      const e = m.get(v.red) ?? { rango: 0, fuera: 0 };
      if (v.enRango) e.rango += 1; else e.fuera += 1;
      m.set(v.red, e);
    }
    return [...m.entries()].sort((a, b) => (b[1].rango + b[1].fuera) - (a[1].rango + a[1].fuera));
  }, [todos, vaults, red]);
  const maxEstado = Math.max(1, ...estadoPorRed.map(([, e]) => e.rango + e.fuera));

  // ---- tabla (filtrada por estado) -------------------------------------------
  const filas = useMemo(() => {
    const f = estado === "rango" ? vaults.filter((v) => v.enRango)
      : estado === "fuera" ? vaults.filter((v) => !v.enRango)
      : vaults; // "todas" y "cerradas" (esta pinta otra tabla) usan la lista completa de activas
    return [...f].sort((a, b) => b.valor - a.valor);
  }, [vaults, estado]);

  // ---- piscinas CERRADAS (otra pestaña): filtradas por red, de mayor a menor ganado ------------
  const cerradas = useMemo(() => {
    const cs = (flota.data?.cerradas ?? []).filter((c) => red === "todas" || c.red === red);
    return [...cs].sort((a, b) => b.ganado - a.ganado);
  }, [flota.data?.cerradas, red]);
  const ganadoCerradoTotal = useMemo(() => cerradas.reduce((s, c) => s + c.ganado, 0), [cerradas]);

  const cargando = flota.isPending;

  // ---- series historicas (Parte 2) ------------------------------------------
  const series = useSeries();
  const [periodo, setPeriodo] = useState<Periodo>("dia");
  const barras = useMemo(() => {
    const pr = series.data?.porRed ?? {};
    const arrays = red === "todas" ? Object.values(pr) : [pr[red] ?? []];
    return agrupar(fundirDias(arrays.filter(Boolean) as DiaSerie[][]), periodo);
  }, [series.data, red, periodo]);

  if (viendo) {
    return (
      <div className="dash dash-viendo">
        <DetalleVault direccion={viendo.direccion} red={REDES[viendo.red]} alVolver={() => setViendo(null)} />
      </div>
    );
  }

  return (
    <div className="dash" ref={raiz}>
      <button className="dash-volver" onClick={alVolver}>{t("← Volver")}</button>

      <header className="dash-hero reveal">
        <div className="dash-hero-txt">
          <span className="dash-eyebrow">
            <span className="punto-vivo" aria-hidden="true" />
            {t("Protocolo")} · {t("En vivo")}
          </span>
          <h1>{t("Dashboard")}</h1>
          <p className="dash-sub">
            {t("Keellabs sigue cada piscina desde que se abre hasta que se cierra: cada reajuste, cada recuperación, cada comisión cobrada. Todo leído en vivo de la cadena, sin intermediarios. Hecho para que tu dinero gane, no solo para que se mueva.")}
          </p>
        </div>
        <dl className="dash-hero-meta">
          <div className="meta-card">
            <span className="meta-ico ico-redes" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><ellipse className="orbita" cx="12" cy="12" rx="4" ry="9" /><path d="M3 12h18" /></svg>
            </span>
            <dt>{t("Redes")}</dt><dd><Contador n={redesActivas().length} /></dd>
          </div>
          <div className="meta-card">
            <span className="meta-ico ico-piscinas" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path className="ola o1" d="M2 9c2.5 0 2.5 2 5 2s2.5-2 5-2 2.5 2 5 2 2.5-2 5-2" /><path className="ola o2" d="M2 15c2.5 0 2.5 2 5 2s2.5-2 5-2 2.5 2 5 2 2.5-2 5-2" /></svg>
            </span>
            <dt>{t("Piscinas abiertas")}</dt><dd><Contador n={todos.length} /></dd>
          </div>
          <div className="meta-card">
            <span className="meta-ico ico-vivo" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M20 12a8 8 0 1 1-2.34-5.66" /><path d="M20 4v4h-4" /></svg>
            </span>
            <dt>{t("Actualizado")}</dt><dd className="dd-vivo"><Hace ts={flota.dataUpdatedAt} /></dd>
          </div>
        </dl>
      </header>

      {/* selector de red: control segmentado con desplazamiento horizontal en móvil */}
      <nav className="dash-redes reveal" aria-label={t("Filtrar por red")}>
        <button className={`dash-red ${red === "todas" ? "sel" : ""}`} onClick={() => setRed("todas")} aria-pressed={red === "todas"}>
          <span className="dash-red-nombre">{t("Todas")}</span>
          <span className="dash-red-cnt">{todos.length}</span>
        </button>
        {redesActivas().map((r) => {
          const n = todos.filter((v) => v.red === r.clave).length;
          const sinDato = flota.redesSinDato?.includes(r.clave) ?? true;
          return (
            <button key={r.clave} className={`dash-red ${red === r.clave ? "sel" : ""} ${n === 0 ? "vacia" : ""}`}
              onClick={() => setRed(r.clave)} aria-pressed={red === r.clave}>
              <IconoRed clave={r.clave} size={18} />
              <span className="dash-red-nombre">{NOMBRE_RED[r.clave]}</span>
              <span className={`dash-red-cnt ${sinDato ? "cargando" : ""}`} title={sinDato ? t("Leyendo esta red…") : undefined}>{sinDato ? "…" : n}</span>
            </button>
          );
        })}
      </nav>

      {flota.data?.redesCaidas.length ? (
        <p className="dash-aviso">{t("No pude leer {redes}: queda fuera de las cifras.", { redes: flota.data.redesCaidas.join(" ni ") })}</p>
      ) : null}

      {/* KPIs */}
      <div className="dash-kpis">
        {cargando ? (
          [0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="kpi">
              <div className="esqueleto-b" style={{ height: 12, width: "45%" }} />
              <div className="esqueleto-b" style={{ height: 30, width: "70%", marginTop: 14 }} />
              <div className="esqueleto-b" style={{ height: 11, width: "85%", marginTop: 12 }} />
            </div>
          ))
        ) : (
          <>
            <div className="kpi reveal c-coral">
              <span className="kpi-ico" aria-hidden><IcoKpi n="capital" /></span>
              <div className="k">{t("Capital gestionado")}</div>
              <div className="v acento">{money(k.total)}</div>
              {red === "todas" && porRed.length > 1 ? (
                <div className="kpi-mini">
                  {porRed.slice(0, 4).map(([clave, e]) => (
                    <div className="kpi-mini-fila" key={clave}>
                      <span className="n"><IconoRed clave={clave} size={14} />{CHAIN(clave)}</span>
                      <span className="repbar" title={t("{pct}% del capital", { pct: Math.round((e.valor / (k.total || 1)) * 100) })}>
                        <i style={{ width: `${Math.max(2, (e.valor / maxRedValor) * 100)}%` }} />
                      </span>
                      <span className="c">{money(e.valor)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="s">{t("valorado al precio de ahora")}</div>
              )}
            </div>

            <div className="kpi reveal c-oro">
              <span className="kpi-ico" aria-hidden><IcoKpi n="capital" /></span>
              <div className="k">{t("Volumen del protocolo")}</div>
              <div className="v">{money(k.volumen)}</div>
              <div className="s">{t("capital total depositado a lo largo del tiempo, a coste")}</div>
            </div>

            <div className="kpi reveal c-lav">
              <span className="kpi-ico" aria-hidden><IcoKpi n="posiciones" /></span>
              <div className="k">{t("Posiciones abiertas")}</div>
              <div className="v">{k.n}</div>
              <div className="s">
                {t("en")} <b>{k.redes}</b> {t("de {total} redes", { total: redesActivas().length })} · {t("{monto} de media", { monto: money(k.medio) })}
              </div>
            </div>

            <div className="kpi reveal c-verde">
              <span className="kpi-ico" aria-hidden><IcoKpi n="rango" /></span>
              <div className="k">{t("Dentro de rango")}</div>
              <div className="v verde">{k.pctRango}%</div>
              <div className="s"><b>{k.enRango}</b> {t("cobrando comisiones · {fuera} fuera de rango", { fuera: k.fuera })}</div>
            </div>

            <div className="kpi reveal c-verde">
              <span className="kpi-ico" aria-hidden><IcoKpi n="ganancias" /></span>
              <div className="k">{t("Ganancias generadas")}</div>
              <div className="v verde">{money(k.ganado)}</div>
              <div className="s">{t("para los dueños, desde que abrieron")}</div>
            </div>

            <div className="kpi reveal c-lav">
              <span className="kpi-ico" aria-hidden><IcoKpi n="medio" /></span>
              <div className="k">{t("Valor medio por posición")}</div>
              <div className="v">{money(k.medio)}</div>
              <div className="s">{t("capital de trabajo puesto por posición")}</div>
            </div>

            <div className="kpi reveal c-coral">
              <span className="kpi-ico" aria-hidden><IcoKpi n="comision" /></span>
              <div className="k">{t("Comisión de Keellabs")}</div>
              <div className="v acento">10%</div>
              <div className="s">{t("de las ganancias,")} <b>{t("nunca del capital")}</b></div>
            </div>
          </>
        )}
      </div>

      {/* graficos de distribucion */}
      <div className="dash-grid2">
        <div className="panel reveal">
          <div className="panel-cab">
            <div className="panel-tit"><span className="panel-ico c-coral" aria-hidden><IcoKpi n="barras" /></span><h2>{t("Capital por par")}</h2></div>
            <span className="tag">{t("Top {n}", { n: porPar.length })}</span>
          </div>
          <p className="panel-sub">{t("Dónde está realmente el dinero, par por par.")}</p>
          {porPar.length ? (
            <div className="barras-v">
              {porPar.map((p) => (
                <div className="barra-v" key={`${p.par}-${p.red}`}>
                  <div className="col" data-val={money(p.valor)} style={{ height: `${Math.max(4, (p.valor / maxPar) * 100)}%` }} />
                  <ParMonedas s0={p.s0} s1={p.s1} size={22} />
                  <div className="lab"><b>{p.par}</b>{CHAIN(p.red)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dash-vacio">{t("Sin posiciones que mostrar.")}</div>
          )}
        </div>

        <div className="panel reveal">
          <div className="panel-cab">
            <div className="panel-tit"><span className="panel-ico c-verde" aria-hidden><IcoKpi n="redes" /></span><h2>{t("Posiciones por red")}</h2></div>
            <span className="tag">{t("{n} en total", { n: k.n })}</span>
          </div>
          <p className="panel-sub">{t("Cuántas están cobrando y cuántas se han salido del rango.")}</p>
          {estadoPorRed.length ? (
            <>
              <div className="barras-h">
                {estadoPorRed.map(([clave, e]) => {
                  const tot = e.rango + e.fuera;
                  return (
                    <div className="barra-h" key={clave}>
                      <div className="n">{CHAIN(clave)}<small>{tot === 1 ? t("{n} posición", { n: tot }) : t("{n} posiciones", { n: tot })}</small></div>
                      <div className="pista" style={{ width: `${(tot / maxEstado) * 100}%`, minWidth: 60 }}>
                        {e.rango > 0 && <div className="seg ok" style={{ width: `${(e.rango / tot) * 100}%` }}>{e.rango}</div>}
                        {e.fuera > 0 && <div className="seg fuera" style={{ width: `${(e.fuera / tot) * 100}%` }}>{e.fuera}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="leyenda">
                <span><i className="ok" /> {t("Dentro de rango (cobrando)")}</span>
                <span><i className="fuera" /> {t("Fuera de rango")}</span>
              </div>
            </>
          ) : (
            <div className="dash-vacio">{t("Sin posiciones que mostrar.")}</div>
          )}
        </div>
      </div>

      {/* tabla de piscinas */}
      <div className="panel reveal">
        <div className="dash-tabla-cab">
          <div>
            <h2>{estado === "cerradas" ? t("Piscinas cerradas") : t("Piscinas activas")}</h2>
            <p className="panel-sub" style={{ margin: "6px 0 0" }}>
              {estado === "cerradas"
                ? t("Posiciones ya cerradas y lo que repartieron a sus dueños mientras vivieron.")
                : t("Cada posición abierta, de mayor a menor capital.")}
            </p>
          </div>
          <div className="dash-filtros">
            <button className={`dash-filtro ${estado === "todas" ? "sel" : ""}`} onClick={() => setEstado("todas")}>{t("Todas")} {vaults.length}</button>
            <button className={`dash-filtro ok ${estado === "rango" ? "sel" : ""}`} onClick={() => setEstado("rango")}>{t("En rango")} {vaults.filter((v) => v.enRango).length}</button>
            <button className={`dash-filtro fuera ${estado === "fuera" ? "sel" : ""}`} onClick={() => setEstado("fuera")}>{t("Fuera")} {vaults.filter((v) => !v.enRango).length}</button>
            <button className={`dash-filtro ${estado === "cerradas" ? "sel" : ""}`} onClick={() => setEstado("cerradas")}>{t("Cerradas")} {cerradas.length}</button>
          </div>
        </div>

        {cargando ? (
          <div className="esqueleto-b" style={{ height: 240, borderRadius: 12 }} />
        ) : estado === "cerradas" ? (
          cerradas.length ? (
            <div className="tabla-scroll">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("Par")}</th><th>{t("Red")}</th><th>{t("Duró")}</th><th>{t("Capital")}</th><th>{t("Ganado")}</th>
                  </tr>
                </thead>
                <tbody>
                  {cerradas.map((c) => <FilaCerrada key={c.direccion} c={c} />)}
                </tbody>
                <tfoot>
                  <tr className="dash-total-fila">
                    <td colSpan={5}>{t("Total repartido por las {n} cerradas", { n: cerradas.length })}</td>
                    <td className="sube"><b>+{num(ganadoCerradoTotal, 2)}</b></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="dash-vacio">{red !== "todas" ? t("Todavía no hay piscinas cerradas en esta red.") : t("Todavía no hay piscinas cerradas.")}</div>
          )
        ) : filas.length ? (
          <div className="tabla-scroll">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("Par")}</th><th>{t("Red")}</th><th>{t("Capital")}</th><th>{t("Trabajando")}</th><th>{t("Ganado")}</th><th>{t("Estado")}</th><th>{t("Enlaces")}</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((v) => (
                  <FilaVault key={v.direccion} v={v} alVer={() => setViendo(v)} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dash-vacio">
            {vaults.length ? t("Ninguna posición con ese estado.") : t("Todavía no hay posiciones abiertas en esta red.")}
          </div>
        )}
      </div>

      {/* SERIES HISTORICAS (Parte 2) — recuentos diarios agregados por el indexer */}
      <div className="dash-hist-cab reveal">
        <div>
          <h2 className="dash-hist-tit">{t("Series históricas")}</h2>
          <p className="panel-sub" style={{ margin: "6px 0 0" }}>
            {red !== "todas" ? t("La actividad del protocolo a lo largo del tiempo · {red}", { red: CHAIN(red) }) : t("La actividad del protocolo a lo largo del tiempo.")}
          </p>
        </div>
        <div className="dash-periodo">
          {(["dia", "semana", "mes"] as Periodo[]).map((p) => (
            <button key={p} className={`dash-periodo-b ${periodo === p ? "sel" : ""}`} onClick={() => setPeriodo(p)}>
              {p === "dia" ? t("Diario") : p === "semana" ? t("Semanal") : t("Mensual")}
            </button>
          ))}
        </div>
      </div>

      {series.isPending ? (
        <div className="esqueleto-b" style={{ height: 220, borderRadius: 16 }} />
      ) : series.data?.hay && barras.length ? (
        <div className="dash-grid3">
          <PanelSerie titulo={t("Rebalanceos")} sub={t("Reajustes ejecutados por el robot")} barras={barras} campo="rebal" ico="rebal" color="c-coral" />
          <PanelSerie titulo={t("Posiciones nuevas")} sub={t("Piscinas abiertas o reabiertas")} barras={barras} campo="opened" ico="nuevas" color="c-lav" />
          <PanelSerie titulo={t("Repartos de comisión")} sub={t("Cobros de comisiones registrados")} barras={barras} campo="fees" ico="comision" color="c-verde" />
        </div>
      ) : (
        <div className="panel reveal">
          <div className="dash-vacio">
            {series.data && !series.data.hay
              ? t("Las series históricas se activan cuando el indexer publique los datos de cada red.")
              : t("Sin actividad registrada en este periodo.")}
          </div>
        </div>
      )}
    </div>
  );
}

function PanelSerie({
  titulo, sub, barras, campo, ico, color,
}: {
  titulo: string; sub: string; barras: Barra[];
  campo: "rebal" | "opened" | "fees" | "recover"; ico?: string; color?: string;
}) {
  const t = useT();
  const max = Math.max(1, ...barras.map((b) => b[campo]));
  const total = barras.reduce((s, b) => s + b[campo], 0);
  const cada = Math.max(1, Math.ceil(barras.length / 6)); // etiquetas del eje X, espaciadas
  return (
    <div className="panel reveal">
      <div className="panel-cab">
        <div className="panel-tit">
          {ico && <span className={`panel-ico ${color ?? ""}`} aria-hidden><IcoKpi n={ico} /></span>}
          <h2>{titulo}</h2>
        </div>
        <span className="tag">{t("{total} total", { total })}</span>
      </div>
      <p className="panel-sub">{sub}</p>
      <div className="serie">
        {barras.map((b, i) => (
          <div className="serie-col" key={i} title={`${b.label}: ${b[campo]}`}>
            <div className="serie-bar" style={{ height: `${(b[campo] / max) * 100}%` }} />
            <span className="serie-x">{i % cada === 0 ? b.label : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilaVault({ v, alVer }: { v: VaultPublico; alVer: () => void }) {
  const t = useT();
  const g = v.ganado;
  return (
    <tr>
      <td className="c-par"><span className="par-cell"><ParMonedas s0={v.s0} s1={v.s1} size={24} />{v.par}</span></td>
      <td className="c-red"><span><IconoRed clave={v.red} size={15} />{CHAIN(v.red)}</span></td>
      <td>{num(v.capital, 2)} <span className="tenue">{v.simboloRef}</span></td>
      <td className="tenue">{antiguedad(v.abierta) ?? "—"}</td>
      <td className={g === undefined ? "tenue" : g > 0 ? "sube" : g < 0 ? "baja" : ""}>
        {g === undefined ? "—" : (
          <span className="ganado-cell">
            <b>{g > 0 ? "+" : ""}{num(g, Math.abs(g) < 1 ? 4 : 2)}</b>
            {v.capital > 0 && (
              <i className="ganado-pct">{g > 0 ? "+" : ""}{num((g / v.capital) * 100, 2)}%</i>
            )}
          </span>
        )}
      </td>
      <td>
        <span className={`estado ${v.enRango ? "ok" : "fuera"}`}>{v.enRango ? t("Cobrando") : t("Fuera")}</span>
      </td>
      {/* A dónde va cada uno: la POOL al mercado en su DEX, y el VAULT (el «hash» del contrato) al
          explorador para auditarlo. Son datos públicos on-chain, no acciones sobre el dinero. */}
      <td className="c-enlaces">
        <span className="dash-enlaces">
          <button className="dash-ver" onClick={alVer} title={t("Ver esta piscina por dentro, en modo lectura")}>
            {t("Ver")} <span aria-hidden="true">→</span>
          </button>
          <a href={urlPool(REDES[v.red], v.pool)} target="_blank" rel="noreferrer"
            title={t("Ver la pool en {sitio}", { sitio: sitioPool(REDES[v.red]) })}>{t("Pool ↗")}</a>
          <a href={urlArkham(v.direccion)} target="_blank" rel="noreferrer"
            title={t("Todo lo que ha hecho este vault, con sus flujos de dinero, en Arkham")}>{t("Arkham ↗")}</a>
        </span>
      </td>
    </tr>
  );
}

/** Una piscina YA CERRADA en la pestaña «Cerradas»: par, red, cuánto vivió, con cuánto empezó y
 *  terminó, y lo que ganó (con %). Todo en la moneda de referencia del vault. */
function FilaCerrada({ c }: { c: VaultCerrada }) {
  const [s0 = "", s1 = ""] = c.par.split("/");
  const g = c.ganado;
  return (
    <tr>
      <td className="c-par"><span className="par-cell"><ParMonedas s0={s0} s1={s1} size={24} />{c.par}</span></td>
      <td className="c-red"><span><IconoRed clave={c.red} size={15} />{CHAIN(c.red)}</span></td>
      <td className="tenue">{duracion(c.duro) ?? "—"}</td>
      <td>{c.capital > 0 ? <>{num(c.capital, 2)} <span className="tenue">{c.simboloRef}</span></> : "—"}</td>
      <td className={g > 0 ? "sube" : g < 0 ? "baja" : ""}>
        <span className="ganado-cell">
          <b>{g > 0 ? "+" : ""}{num(g, Math.abs(g) < 1 ? 4 : 2)}</b>
          {c.pct !== undefined && (
            <i className="ganado-pct">{c.pct > 0 ? "+" : ""}{num(c.pct, 2)}%</i>
          )}
        </span>
      </td>
    </tr>
  );
}
